import type dayjs from 'dayjs';
import type plugin from 'dayjs/plugin/duration';
import type { VueI18n } from 'vue-i18n';

export function useTime() {
  const $dayjs = useNuxtApp().$dayjs as typeof dayjs;
  const $t = useNuxtApp().$t as VueI18n['t'];

  const timeFormat = (date?: Date | string, format = 'YYYY-MM-DD HH:mm:ss'): string =>
    date ? $dayjs(date).format(format) : '';

  function timezoneDayjs(timestamp: number) {
    return $dayjs.tz(timestamp * 1000).utc(true);
  }

  function timezoneFormat(timestamp: number, format = 'YYYY-MM-DD HH:mm:ss') {
    if (timestamp === 0) {
      return '-';
    }

    return timezoneDayjs(timestamp).format(format);
  }

  const timeFormatText = (time: number): string => {
    let timeStr = time.toString();
    const zerosToAdd = 13 - timeStr.length; // 处理可能有的传参单位是秒和毫秒的情况
    if (zerosToAdd > 0) {
      timeStr = timeStr + '0'.repeat(zerosToAdd);
    }

    const timeNum = parseInt(timeStr);

    const current = Date.now();
    const timeDifference = current - timeNum;
    const minute = Math.floor(timeDifference / (1000 * 60));
    const hour = Math.floor(timeDifference / (1000 * 60 * 60));
    const days = Math.floor(hour / 24);
    const years = days / 365;
    const currentYear = new Date().getFullYear();
    const lastYear = new Date(timeNum).getFullYear();
    if (currentYear - lastYear >= 1) {
      return $dayjs(timeNum).format('YYYY-MM-DD');
    }

    if (minute <= 1) {
      return $t('gang-gang');
    } else if (minute > 1 && minute <= 60) {
      return $t('minute-fen-zhong-qian', [minute]);
    } else if (minute > 60 && hour < 24) {
      return $t('hour-xiao-shi-qian', [hour]);
    } else if (hour >= 24 && days < 7) {
      return $t('days-tian-qian', [days]);
    } else if (days >= 7 && years < 1) {
      return $dayjs(timeNum).format($t('mm-yue-dd-ri'));
    } else {
      return $dayjs(timeNum).format('YYYY-MM-DD');
    }
  };

  const formatDuration = (time: number, customFormat?: (d: plugin.Duration) => string) => {
    const d = $dayjs.duration(time);
    if (customFormat) {
      return customFormat(d);
    }
    let builtinFormat = 'mm:ss';
    if (d.hours() > 0) {
      builtinFormat = `HH:${builtinFormat}`;
    }
    return d.format(builtinFormat);
  };

  const getFormateTime = (milliseconds = 0, format = 'HH:mm:ss') => {
    const safeMilliseconds = Number.isFinite(milliseconds) ? Math.max(milliseconds, 0) : 0;
    const roundedMilliseconds =
      safeMilliseconds === 0 ? 0 : Math.ceil(safeMilliseconds / (60 * 1000)) * 60 * 1000;
    // dayjs.extend(duration)
    const d = $dayjs.duration(roundedMilliseconds);
    let builtinFormat = 'ss';

    if (d.minutes() > 0) {
      builtinFormat = `mm:${builtinFormat}`;
    }
    if (d.hours() > 0) {
      builtinFormat = `HH:${builtinFormat}`;
    }

    return d.format(format || builtinFormat);
  };

  return {
    $dayjs: $dayjs as typeof dayjs,
    timeFormat,
    timeFormatText,
    getFormateTime,
    timezoneFormat,
    formatDuration,
  };
}
